// Has this client paid? The dashboard's copy of the ONE answer.
//
// MIRRORS worker/src/lib/paidState.ts `paidState` exactly — the levels
// (none | receipt_pending | advance | full), the evidence that sets each, the
// amount received and what is left. worker/src/lib/paidState.parity.test.ts
// holds the two together case for case. Change one, change both.
//
// Owner, 2026-10-06: « One meaning each, read by everything: the agent, the
// reminders, the pipeline, the dashboard. » Before this file the orders list
// chip read paid_in_full | advance_received_amount and was blind to the AMEEX
// card's acompte (45 orders), the printer button read paid_at | status, and
// the « Paiement requis » flag read paid_in_full | paid_at.

export type PaidLevel = 'none' | 'receipt_pending' | 'advance' | 'full';

/** Lowest to highest. */
export const PAID_LEVELS: readonly PaidLevel[] = ['none', 'receipt_pending', 'advance', 'full'];

/** Is `level` at or above `min`? */
export function paidAtLeast(level: PaidLevel, min: PaidLevel): boolean {
  return PAID_LEVELS.indexOf(level) >= PAID_LEVELS.indexOf(min);
}

/** One `payment_proposals` row, as far as the level needs it. */
export interface PaidProposal {
  status?: string | null;
  amount?: number | string | null;
}

/** One `cash_adjustments` row linked to the order (`order_id`). */
export interface PaidCashLine {
  amount?: number | string | null;
}

/**
 * Everything the level is read from. Every field optional: a caller that did
 * not read one gets the answer the others give, never a guess.
 */
export interface PaidFacts {
  status?: string | null;
  total_price?: number | string | null;
  /** Same as total_price — the AMEEX helpers call it `total`. */
  total?: number | string | null;
  paid_at?: string | null;
  paid_in_full?: boolean | null;
  advance_received_amount?: number | string | null;
  advance_received_at?: string | null;
  advance_amount?: number | string | null;
  advance_proof_url?: string | null;
  ameex_status?: string | null;
  /** Read from payment_proposals, when the caller has them. */
  proposals?: ReadonlyArray<PaidProposal> | null;
  /** Read from cash_adjustments (order_id = this order), when the caller has them. */
  cash_lines?: ReadonlyArray<PaidCashLine> | null;
}

export interface PaidState {
  level: PaidLevel;
  /** MAD recorded as received by a trusted path. 0 when only a date or a status says money came. */
  received: number;
  /** What the client still owes in MAD (0 at full); null when the price is unknown. */
  owed: number | null;
  /** Which facts set the level, in plain words — for logs and the notes a person reads. */
  evidence: string[];
}

const num = (v: unknown): number => {
  if (v == null || v === '') return NaN;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
};

const set = (v: unknown): boolean => String(v ?? '').trim() !== '';

/**
 * The amount the ORDER'S OWN COLUMNS say was received, in MAD.
 *
 * ⚠️ THE RECEIVED COLUMN WINS, EVEN AT 0. `advance_received_amount` is written
 * by a person accepting a receipt or by « Acompte reçu »; `advance_amount` is
 * the AMEEX card's « Acompte déjà reçu ». When both are set, the explicit
 * « received » record is the fact (unchanged from lib/orderMoney.ts, 09-29).
 */
export function columnAmount(o: PaidFacts | null | undefined): number {
  if (!o) return 0;
  const recorded = o.advance_received_amount ?? o.advance_amount;
  const n = num(recorded);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Delivered by the courier — or marked delivered by a person. */
export function isDelivered(o: PaidFacts | null | undefined): boolean {
  if (!o) return false;
  return String(o.ameex_status ?? '').trim() === 'delivered' || String(o.status ?? '').trim() === 'delivered';
}

/**
 * THE answer. Pure: the same row gives the same level in the worker, the
 * dashboard and a test.
 */
export function paidState(o: PaidFacts | null | undefined): PaidState {
  if (!o) return { level: 'none', received: 0, owed: null, evidence: [] };

  const total = num(o.total_price ?? o.total);
  const evidence: string[] = [];

  const proposals = o.proposals ?? [];
  const accepted = proposals.filter((p) => String(p.status ?? '') === 'accepted');
  const pending = proposals.filter((p) => String(p.status ?? '') === 'pending');
  const decidedNo = proposals.filter((p) => ['rejected', 'cancelled'].includes(String(p.status ?? '')));

  const acceptedSum = accepted.reduce((s, p) => s + (num(p.amount) > 0 ? num(p.amount) : 0), 0);
  const cashNet = (o.cash_lines ?? []).reduce((s, l) => s + (Number.isFinite(num(l.amount)) ? num(l.amount) : 0), 0);

  const fromColumns = columnAmount(o);
  // The SAME money is often written in two places (the order and the caisse);
  // the largest record is what she paid, never their sum.
  const received = Math.max(fromColumns, acceptedSum, cashNet > 0 ? cashNet : 0);
  const round = (n: number) => Math.round(n * 100) / 100;

  if (fromColumns > 0) evidence.push(`${round(fromColumns)} MAD recorded on the order`);
  if (accepted.length) evidence.push('a receipt accepted by a person');
  if (cashNet > 0) evidence.push(`${round(cashNet)} MAD in the caisse for this order`);
  if (set(o.paid_at)) evidence.push('a payment date is recorded');
  if (String(o.status ?? '').trim() === 'paid') evidence.push('status « Payée »');

  // ── full ──
  const full = o.paid_in_full === true
    || (Number.isFinite(total) && total > 0 && received >= total)
    || isDelivered(o);
  if (full) {
    if (o.paid_in_full === true) evidence.unshift('paid in full');
    else if (isDelivered(o) && !(Number.isFinite(total) && total > 0 && received >= total)) {
      evidence.unshift('delivered — the courier collected the rest');
    } else evidence.unshift('the amount received covers the price');
    return { level: 'full', received: round(received), owed: 0, evidence };
  }

  const owed = Number.isFinite(total) && total > 0 ? round(Math.max(0, total - received)) : null;

  // ── advance ──
  if (received > 0 || set(o.paid_at) || String(o.status ?? '').trim() === 'paid' || accepted.length > 0) {
    return { level: 'advance', received: round(received), owed, evidence };
  }

  // ── receipt_pending ──
  // A proof on the order with nothing recorded: the receipt sweep filed it (it
  // writes the proof with its proposal) or a person attached it. Until a
  // person decides, nobody asks her again. A receipt a person REJECTED, with
  // nothing else waiting, is no longer a reason to wait.
  const proofWaiting = set(o.advance_proof_url) && !(decidedNo.length > 0 && pending.length === 0);
  if (pending.length > 0 || proofWaiting) {
    return {
      level: 'receipt_pending',
      received: 0,
      owed,
      evidence: [pending.length > 0 ? 'a receipt is waiting for a person to check' : 'a payment proof is attached, no amount recorded yet'],
    };
  }

  return { level: 'none', received: 0, owed, evidence: [] };
}

/**
 * A truthy « money came » marker for the readers whose input is a `paidAt`
 * (upsell, album add, resume plan, open loops…): the payment date when there
 * is one, else when the advance was recorded, else 'recorded'. Null at none
 * and at receipt_pending — a slip nobody checked has not moved money.
 */
export function paidSince(o: PaidFacts | null | undefined): string | null {
  if (!o || !paidAtLeast(paidState(o).level, 'advance')) return null;
  return String(o.paid_at ?? '').trim() || String(o.advance_received_at ?? '').trim() || 'recorded';
}


/** The chip a person reads, in French (the admin is translated by t()). */
export function paidLabel(s: PaidState): string {
  switch (s.level) {
    case 'full': return 'Payé';
    case 'advance': return s.received > 0 ? `Avance ${Math.round(s.received)}` : 'Paiement enregistré';
    case 'receipt_pending': return 'Reçu à vérifier';
    default: return '';
  }
}

/**
 * May this order go to the printer? Money in (an advance at least), or a
 * status at/after « Payée » — the worker's isPaidOrder (lib/marketingShared.ts),
 * which is the print gate the server applies.
 */
const PAID_OR_LATER = new Set(['paid', 'ready-to-print', 'printed', 'in-production', 'ready-to-deliver', 'shipped', 'delivered']);
export function paidForPrint(o: PaidFacts | null | undefined): boolean {
  if (!o) return false;
  return paidAtLeast(paidState(o).level, 'advance') || PAID_OR_LATER.has(String(o.status ?? ''));
}
